/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { publicHttpClient } from '@ahm/api-wrappers-core';
import { hasRefreshToken, shouldAttachBearer } from './auth-interceptor-shared';
import { getAuthToken } from './get-token';
import { terminalLogout } from './terminal-logout';

let installed = false;

/**
 * Interceptor cho WEB LOGIN THƯỜNG (OAuth callback set cả `token` + `refresh_token`).
 *
 * ⚠️ KHÔNG dùng cho webinapp — webinapp login bằng token trên URL nên KHÔNG có
 * refresh_token. Webinapp dùng `installWebinappAuthInterceptor`
 * (webinapp-auth-interceptor.ts). `new-auth-provider` chọn đúng 1 interceptor để
 * cài theo loại session.
 *
 * KHÔNG còn refresh chain: token web sống 3 ngày; refresh runtime đã bỏ (từng gây
 * vòng lặp authen khi refresh lỗi). `refresh_token` cookie giờ chỉ là MARKER phân
 * loại web vs webinapp (hasRefreshToken), không dùng để refresh nữa.
 *
 * Request interceptor:
 *  - Attach Bearer từ cookie `token` (đọc thẳng, không pre-emptive refresh).
 *  - Idempotent: không override Authorization header đã tự set.
 *
 * Response interceptor:
 *  - 401 + là web session (có refresh_token) → terminalLogout (phiên hết hạn).
 *  - 401 + anonymous (không refresh_token) → reject im lặng (API cần đăng nhập,
 *    không phải "hết hạn"; caller tự prompt login).
 */
export function installAuthAxiosInterceptor() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  publicHttpClient.interceptors.request.use((config) => {
    if (
      config.headers &&
      !config.headers.Authorization &&
      shouldAttachBearer(config.url)
    ) {
      const token = getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  });

  publicHttpClient.interceptors.response.use(
    (res) => res,
    async (error) => {
      const status = error?.response?.status;
      const original = error?.config;
      if (status !== 401 || !original || !shouldAttachBearer(original.url)) {
        return Promise.reject(error);
      }

      // Anonymous (chưa từng web-login → KHÔNG có refresh_token): 401 nghĩa là
      // API này cần đăng nhập, KHÔNG phải "phiên hết hạn". KHÔNG terminalLogout
      // (tránh toast "hết hạn" + đá về '/'). Reject im lặng — caller tự xử lý.
      if (!hasRefreshToken()) {
        return Promise.reject(error);
      }

      // Web session, token JWT hết hạn → đăng xuất dứt điểm (không refresh/retry).
      terminalLogout();
      return Promise.reject(error);
    }
  );
}
